import { config } from '@react-spring/web';
import classNames from 'classnames';
import { omit } from 'lodash-es';
import type { ComponentType, PropsWithChildren, ReactNode } from 'react';
import { BaseModal } from './base-modal';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  size: 'small' | 'large';
  dismissable?: boolean;
};

export const Modal = ({ children, isOpen, onClose, size, className, dismissable = true }: PropsWithChildren<ModalProps>) => (
  <BaseModal
    isOpen={isOpen}
    onClose={onClose}
    className={classNames(
      'tds-modal top-1/2 left-1/2 flex flex-col px-200 md:px-300', // vertical paddings defined in css to include safe-areas
      {
        'tds-modal-small': size === 'small',
        'tds-modal-large': size === 'large',
      },
      className,
    )}
    animation={{
      from: { y: -20, translateX: '-50%', translateY: '-50%' },
      enter: { y: 0, translateX: '-50%', translateY: '-50%' },
      leave: { y: -20, translateX: '-50%', translateY: '-50%' },
      config: config.stiff,
    }}
    dismissable={dismissable}
    zIndexIncrease={10}>
    {children}
  </BaseModal>
);

type AsModalProps = Pick<ModalProps, 'className' | 'dismissable' | 'size'>;
type MandatoryModalProps = Pick<ModalProps, 'isOpen' | 'onClose'>;

export function asModal<P>(modalProps: AsModalProps, Content: ComponentType<P>) {
  return (props: P & MandatoryModalProps) => (
    <Modal {...modalProps} isOpen={props.isOpen} onClose={props.onClose}>
      <Content {...props} />
    </Modal>
  );
}

type ModalContentProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
  className?: string;
  as?: 'section' | 'form' | 'article';
};

export const ModalContent = ({ as: Element = 'section', className, ...props }: PropsWithChildren<ModalContentProps>) => (
  <Element className={classNames('overflow-y-auto tds-modal-content flex-1', className)} {...omit(props, 'ref')} />
);

type ModalSectionProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
  className?: string;
  as?: 'section' | 'form' | 'article' | 'ul';
};

export const ModalSection = ({ as: Element = 'section', className, ...props }: PropsWithChildren<ModalSectionProps>) => (
  <Element className={classNames('tds-modal-section p-300', className)} {...omit(props, 'ref')} />
);

export const ModalActionBar = ({ children, cssClassNames }: { children: ReactNode; cssClassNames?: string }) => {
  return cssClassNames === undefined ?
    <footer className="flex flex-col md:flex-row md:flex-wrap md:content-end md:justify-end gap-100">{children}</footer> :
    <footer className={cssClassNames}>{children}</footer>;
};
